/**
 * M9 CH-6: securing a hulk (plan `docs/plans/2026-10-05-m9-claimable-hulls.md` §4.3). A hulk's hazards and squatters
 * come from its survey roll (`survey.ts hulkHazards`, by region and hash); clearing them reuses M7's S40 rules
 * (`story-link/reactivation/restore.ts`) over `career.hulks.sites[key]` instead of a reactivation record:
 *
 * - **Coolant** (`ventHulk`): the same rule as `ventCoolant`: from outside `salvage.json coolant.radius` the cut is
 *   safe, closer in the ship takes `coolant.damage` (`hullDamage`, the caller applies it); either way the site is
 *   `vented`. Venting twice changes nothing.
 * - **Squatters** (Wreckers only; **no Holdfast family at a hulk**): paid off with the region's fence fee
 *   (`payHulkFence`, wallet checked first, the Career's wallet only: settlement exactly once) or fought with M7's
 *   squat waves (`hulkSquatWaves` = `squatWaves`, the region's M3 raider waves); the caller marks the fight won with
 *   `markHulkSquatFought` once the waves are gone. `cleared` records which.
 * - **Deliveries wait** (`hulkClearRefusal`): CH-5b's kit and CH-12a's live port call it before taking cargo; an
 *   unvented coolant leak or uncleared squat blocks work, as `restore.ts clearRefusal` does at a derelict.
 *
 * Work needs a surveyed site still in play (`noSite` otherwise: no survey, a finished `done` key, or a claimed site).
 * The messages and refusal lines are M7's (`reactivation/text.json`) except `noSite`, which reads the hulk survey
 * refusal. Career in, Career out; pure. UI-rate events, never per frame.
 */
import { withWallet } from "../career/ops";
import type { Career, HulkCleared, HulkSite } from "../career/types";
import type { RegionId } from "../galaxy/types";
import type { RelinkConfig } from "../relink/config";
import type { ScheduledSpawn } from "../relink/rules";
import {
  COOLANT,
  squatWaves,
  type CoolantConfig,
} from "../story-link/reactivation/restore";
import {
  reactivationText,
  type ReactivationText,
} from "../story-link/reactivation/survey";
import { fillTokens } from "../story-link/world";
import { hulksConfig, type HulksConfig } from "./data";
import { hulkHazards, type HulkHazards, type HulkRef } from "./survey";

export type HulkSecureRefusal =
  "noSite" | "hazard" | "squatters" | "funds" | "noHazard" | "noSquat";

export type HulkSecureResult =
  | { ok: true; career: Career; message: string; hullDamage: number }
  | { ok: false; reason: HulkSecureRefusal };

/** The site open for work: surveyed, still in play (not `done`) and not yet claimed. */
function openSite(career: Career, key: string): HulkSite | null {
  const state = career.hulks;
  if (state === undefined || state.done.includes(key)) return null;
  const site = state.sites[key];
  if (
    site === undefined ||
    site.surveyed === undefined ||
    site.claimedAt !== undefined
  )
    return null;
  return site;
}

function withSite(career: Career, key: string, site: HulkSite): Career {
  const state = career.hulks ?? { done: [], sites: {} };
  return {
    ...career,
    hulks: { done: state.done, sites: { ...state.sites, [key]: site } },
  };
}

/** What still blocks work at the hulk (`restore.ts clearRefusal`'s rule): an unvented leak, an uncleared squat. */
export function siteClearRefusal(
  hazards: HulkHazards,
  site: HulkSite,
): "hazard" | "squatters" | null {
  if (hazards.coolantHazard && site.vented !== true) return "hazard";
  if (hazards.squatters && site.cleared === undefined) return "squatters";
  return null;
}

/** Why kit work at the hulk `ref` is refused (no open surveyed site, a leak, a squat), or null when it may go on. */
export function hulkClearRefusal(
  career: Career,
  ref: HulkRef,
  cfg: HulksConfig = hulksConfig(),
): "noSite" | "hazard" | "squatters" | null {
  const site = openSite(career, ref.key);
  if (site === null) return "noSite";
  return siteClearRefusal(hulkHazards(ref, cfg), site);
}

/**
 * Vents the hulk's coolant leak. `dist2ToAnchor` is the ship's squared distance to the hulk: outside `coolant.radius`
 * the cut is safe, inside it the ship takes `coolant.damage` (`hullDamage`).
 */
export function ventHulk(
  career: Career,
  ref: HulkRef,
  dist2ToAnchor: number,
  coolant: CoolantConfig = COOLANT,
  cfg: HulksConfig = hulksConfig(),
  text: ReactivationText = reactivationText(),
): HulkSecureResult {
  const site = openSite(career, ref.key);
  if (site === null) return { ok: false, reason: "noSite" };
  if (!hulkHazards(ref, cfg).coolantHazard)
    return { ok: false, reason: "noHazard" };
  if (site.vented === true)
    return { ok: true, career, message: text.messages.vented, hullDamage: 0 };
  const next = withSite(career, ref.key, { ...site, vented: true });
  const hot = dist2ToAnchor < coolant.radius * coolant.radius;
  return hot
    ? {
        ok: true,
        career: next,
        message: fillTokens(text.messages.ventedHot, {
          damage: String(coolant.damage),
        }),
        hullDamage: coolant.damage,
      }
    : { ok: true, career: next, message: text.messages.vented, hullDamage: 0 };
}

function clearSquat(
  career: Career,
  ref: HulkRef,
  how: HulkCleared,
  cfg: HulksConfig,
  text: ReactivationText,
): HulkSecureResult {
  const site = openSite(career, ref.key);
  if (site === null) return { ok: false, reason: "noSite" };
  const hazards = hulkHazards(ref, cfg);
  if (!hazards.squatters || site.cleared !== undefined)
    return { ok: false, reason: "noSquat" };
  if (how === "fought")
    return {
      ok: true,
      career: withSite(career, ref.key, { ...site, cleared: how }),
      message: text.messages.fought,
      hullDamage: 0,
    };
  if (career.wallet < hazards.fenceFee) return { ok: false, reason: "funds" };
  const paid = withWallet(career, -hazards.fenceFee);
  return {
    ok: true,
    career: withSite(paid, ref.key, { ...site, cleared: how }),
    message: text.messages.paid,
    hullDamage: 0,
  };
}

/** Pays the hulk's squatters off with the region's fence fee (refused short of funds; the wallet is checked first). */
export function payHulkFence(
  career: Career,
  ref: HulkRef,
  cfg: HulksConfig = hulksConfig(),
  text: ReactivationText = reactivationText(),
): HulkSecureResult {
  return clearSquat(career, ref, "paid", cfg, text);
}

/** The squat was fought off (the caller saw `hulkSquatWaves` gone). */
export function markHulkSquatFought(
  career: Career,
  ref: HulkRef,
  cfg: HulksConfig = hulksConfig(),
  text: ReactivationText = reactivationText(),
): HulkSecureResult {
  return clearSquat(career, ref, "fought", cfg, text);
}

/** The squat's waves: M7's Wrecker squat, the region's raider waves from the M3 reboot table (no guardians). */
export function hulkSquatWaves(
  region: RegionId,
  relink: RelinkConfig,
): ScheduledSpawn[] {
  return squatWaves(region, relink);
}

/**
 * A refusal's player-facing line (M7's lines; `noSite` reads the hulk survey refusal; a hazard or squat that is not
 * there, or already dealt with, reads as done).
 */
export function hulkSecureRefusalText(
  reason: HulkSecureRefusal,
  cfg: HulksConfig = hulksConfig(),
  text: ReactivationText = reactivationText(),
): string {
  switch (reason) {
    case "noSite":
      return cfg.text.refusals.scanner;
    case "noHazard":
      return text.messages.vented;
    case "noSquat":
      return text.messages.fought;
    default:
      return text.refusals[reason];
  }
}
